import { __ } from '@wordpress/i18n';
import { useBlockProps, InspectorControls } from '@wordpress/block-editor';
import {
    PanelBody,
    TextControl,
    TextareaControl,
    SelectControl,
    Button,
    Card,
    CardHeader,
    CardBody,
    Flex,
    FlexItem,
    ToggleControl,
    __experimentalSpacer as Spacer,
} from '@wordpress/components';
import { useState, useEffect } from '@wordpress/element';
import { useSelect } from '@wordpress/data';
import apiFetch from '@wordpress/api-fetch';

export default function Edit({ attributes, setAttributes }) {
    const {
        mainTitle,
        subtitle,
        backgroundColor,
        textColor,
        mobileMaxHeight,
        faqSource,
        faqCategory,
        faqLimit,
        faqs,
        customLink,
        prevText,
        linkText,
        linkUrl,
        fullWidth
    } = attributes;

    const [expandedFaq, setExpandedFaq] = useState(null);

    const blockProps = useBlockProps({
        className: 'faq-section-editor'
    });

    // Fetch FAQ categories using REST API
    const [faqCategories, setFaqCategories] = useState([]);
    const [isLoadingCategories, setIsLoadingCategories] = useState(true);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                setIsLoadingCategories(true);
                console.log('Fetching FAQ categories from REST API...');
                
                // Use the correct REST API endpoint for custom taxonomy
                const categories = await apiFetch({
                    path: '/wp/v2/faq-category?per_page=100&hide_empty=false'
                });
                
                console.log('FAQ Categories from REST API:', categories);
                setFaqCategories(categories || []);
            } catch (error) {
                console.error('Error fetching FAQ categories:', error);
                
                // Try alternative endpoint if the first one fails
                try {
                    console.log('Trying alternative endpoint...');
                    const categories = await apiFetch({
                        path: '/wp/v2/terms/faq-category?per_page=100&hide_empty=false'
                    });
                    console.log('FAQ Categories from alternative endpoint:', categories);
                    setFaqCategories(categories || []);
                } catch (error2) {
                    console.error('Both endpoints failed:', error2);
                    setFaqCategories([]);
                }
            } finally {
                setIsLoadingCategories(false);
            }
        };

        fetchCategories();
    }, []);

    // Debug logging
    useEffect(() => {
        console.log('FAQ Categories from data store:', faqCategories);
        console.log('Is loading categories:', isLoadingCategories);
    }, [faqCategories, isLoadingCategories]);

    // Build category options for dropdown
    const categoryOptions = [
        { label: __('All Categories', 'puroclean'), value: 'all' },
        ...faqCategories.map(category => ({
            label: category.name,
            value: category.slug
        }))
    ];

    const updateFAQ = (index, field, value) => {
        const newFaqs = [...faqs];
        newFaqs[index] = { ...newFaqs[index], [field]: value };
        setAttributes({ faqs: newFaqs });
    };

    const addFAQ = () => {
        const newFaqs = [...faqs, {
            question: __('New FAQ Question', 'puroclean'),
            answer: __('New FAQ Answer', 'puroclean')
        }];
        setAttributes({ faqs: newFaqs });
    };

    const removeFAQ = (index) => {
        if (faqs.length > 1) {
            const newFaqs = faqs.filter((_, i) => i !== index);
            setAttributes({ faqs: newFaqs });
            if (expandedFaq === index) {
                setExpandedFaq(null);
            }
        }
    };

    const moveFAQ = (index, direction) => {
        const newFaqs = [...faqs];
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        
        if (newIndex >= 0 && newIndex < faqs.length) {
            [newFaqs[index], newFaqs[newIndex]] = [newFaqs[newIndex], newFaqs[index]];
            setAttributes({ faqs: newFaqs });
        }
    };

    const backgroundOptions = [
        { label: __('Light Gray', 'puroclean'), value: 'bg-[#F6F6F6]'},
        { label: __('White', 'puroclean'), value: 'bg-white' },
        { label: __('Primary', 'puroclean'), value: 'bg-primary' },
        { label: __('Secondary', 'puroclean'), value: 'bg-secondary' },
    ];

    const textColorOptions = [
        { label: __('Dark Gray', 'puroclean'), value: 'text-gray-900' },
        { label: __('White', 'puroclean'), value: 'text-white' },
        { label: __('Primary', 'puroclean'), value: 'text-primary' },
        { label: __('Secondary', 'puroclean'), value: 'text-secondary' },
    ];

    const heightOptions = [
        { label: __('Small (16rem)', 'puroclean'), value: 'h-64' },
        { label: __('Medium (24rem)', 'puroclean'), value: 'h-96' },
        { label: __('Large (32rem)', 'puroclean'), value: 'h-128' },
        { label: __('Extra Large (40rem)', 'puroclean'), value: 'h-160' },
    ];

    return (
        <div {...blockProps}>
            <InspectorControls>
                <PanelBody title={__('FAQ Settings', 'puroclean')} initialOpen={true}>
                    <TextControl
                        label={__('Main Title', 'puroclean')}
                        value={mainTitle}
                        onChange={(value) => setAttributes({ mainTitle: value })}
                        placeholder={__('Frequently Asked Questions', 'puroclean')}
                    />
                    <Spacer />
                    <TextareaControl
                        label={__('Subtitle', 'puroclean')}
                        value={subtitle}
                        onChange={(value) => setAttributes({ subtitle: value })}
                        placeholder={__('Find answers to common questions about our services', 'puroclean')}
                        rows={3}
                    />
                    <Spacer />
                    <SelectControl
                        label={__('Background Color', 'puroclean')}
                        value={backgroundColor}
                        options={backgroundOptions}
                        onChange={(value) => setAttributes({ backgroundColor: value })}
                    />
                    <Spacer />
                    <SelectControl
                        label={__('Text Color', 'puroclean')}
                        value={textColor}
                        options={textColorOptions}
                        onChange={(value) => setAttributes({ textColor: value })}
                    />
                    <Spacer />
                    <SelectControl
                        label={__('Mobile Max Height', 'puroclean')}
                        value={mobileMaxHeight}
                        options={heightOptions}
                        onChange={(value) => setAttributes({ mobileMaxHeight: value })}
                        help={__('Sets the maximum height of the FAQ container on mobile devices', 'puroclean')}
                    />
                    <Spacer />
                    <ToggleControl
                        label={__('Full Width', 'puroclean')}
                        checked={fullWidth}
                        onChange={(value) => setAttributes({ fullWidth: value })}
                        help={__('Enable to make the FAQ section span full width with no padding, useful for blog posts and other constrained containers', 'puroclean')}
                    />
                </PanelBody>
                
                <PanelBody title={__('FAQ Source', 'puroclean')} initialOpen={false}>
                    <SelectControl
                        label={__('FAQ Source', 'puroclean')}
                        value={faqSource}
                        options={[
                            { label: __('Manual Entry', 'puroclean'), value: 'manual' },
                            { label: __('From FAQ Posts', 'puroclean'), value: 'cpt' }
                        ]}
                        onChange={(value) => setAttributes({ faqSource: value })}
                        help={__('Choose whether to manually enter FAQs or select from existing FAQ posts', 'puroclean')}
                    />
                    
                    {faqSource === 'cpt' && (
                        <>
                            {/* adds an url to the FAQ posts CPT in a new tab so they can be changed */}
                            <a className="text-primary" href={window.siteGlobals?.adminUrl + 'edit.php?post_type=faq'} target="_blank" rel="noopener noreferrer">
                                {__('Edit FAQ Posts', 'puroclean')}
                            </a>
                            <Spacer />
                            <TextControl
                                label={__('FAQ Limit', 'puroclean')}
                                type="number"
                                value={faqLimit}
                                onChange={(value) => setAttributes({ faqLimit: parseInt(value) || 10 })}
                                help={__('Maximum number of FAQs to display (ordered by newest updated first)', 'puroclean')}
                                min="1"
                                max="50"
                            />
                            <Spacer />
                            <SelectControl
                                label={__('FAQ Category', 'puroclean')}
                                value={faqCategory}
                                options={isLoadingCategories ? 
                                    [{ label: __('Loading categories...', 'puroclean'), value: 'loading' }] : 
                                    categoryOptions
                                }
                                onChange={(value) => setAttributes({ faqCategory: value })}
                                help={isLoadingCategories ? 
                                    __('Loading FAQ categories...', 'puroclean') : 
                                    __('Select a specific FAQ category to filter by', 'puroclean')
                                }
                                disabled={isLoadingCategories}
                            />
                        </>
                    )}
                </PanelBody>

                <PanelBody title={__('Call to Action Link', 'puroclean')} initialOpen={false}>
                    <ToggleControl
                        label={__('Use Custom Link', 'puroclean')}
                        checked={customLink}
                        onChange={(value) => setAttributes({ customLink: value })}
                        help={__('Enable to customize the call-to-action link at the bottom of the FAQ section', 'puroclean')}
                    />
                    
                    {customLink && (
                        <>
                            <Spacer />
                            <TextControl
                                label={__('Text Before Link (Optional)', 'puroclean')}
                                value={prevText}
                                onChange={(value) => setAttributes({ prevText: value })}
                                placeholder={__('Still have questions?', 'puroclean')}
                                help={__('Text that appears before the link. Leave blank to use default.', 'puroclean')}
                            />
                            <Spacer />
                            <TextControl
                                label={__('Link Text', 'puroclean')}
                                value={linkText}
                                onChange={(value) => setAttributes({ linkText: value })}
                                placeholder={__('Call us Now', 'puroclean')}
                                help={__('The clickable text for the link', 'puroclean')}
                            />
                            <Spacer />
                            <TextControl
                                label={__('Link URL', 'puroclean')}
                                value={linkUrl}
                                onChange={(value) => setAttributes({ linkUrl: value })}
                                placeholder={__('https://example.com or tel:+1234567890', 'puroclean')}
                                help={__('The URL or phone number the link should go to (use tel: for phone numbers)', 'puroclean')}
                            />
                        </>
                    )}
                </PanelBody>
            </InspectorControls>

            <div className="faq-section-preview p-6 rounded-lg">
                <div className="text-center mb-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">
                        {mainTitle || __('Frequently Asked Questions', 'puroclean')}
                    </h2>
                    {subtitle && (
                        <p className="text-lg text-gray-700 max-w-3xl mx-auto">
                            {subtitle}
                        </p>
                    )}
                </div>

                {faqSource === 'manual' ? (
                    <>
                        <div className="max-w-4xl mx-auto space-y-4">
                            {faqs.map((faq, index) => (
                                <Card key={index} className="faq-item-editor">
                                    <CardHeader>
                                        <Flex justify="space-between" align="center">
                                            <FlexItem>
                                                <strong>{__('FAQ', 'puroclean')} #{index + 1}</strong>
                                                <div className="text-sm text-gray-600 mt-1">
                                                    {__('Anchor ID:', 'puroclean')} #faq-{index + 1}
                                                </div>
                                            </FlexItem>
                                            <FlexItem>
                                                <Flex gap={2}>
                                                    <Button
                                                        icon="arrow-up-alt2"
                                                        disabled={index === 0}
                                                        onClick={() => moveFAQ(index, 'up')}
                                                        size="small"
                                                        label={__('Move up', 'puroclean')}
                                                    />
                                                    <Button
                                                        icon="arrow-down-alt2"
                                                        disabled={index === faqs.length - 1}
                                                        onClick={() => moveFAQ(index, 'down')}
                                                        size="small"
                                                        label={__('Move down', 'puroclean')}
                                                    />
                                                    <Button
                                                        icon={expandedFaq === index ? 'minus' : 'plus-alt2'}
                                                        onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                                                        size="small"
                                                        label={expandedFaq === index ? __('Collapse', 'puroclean') : __('Expand', 'puroclean')}
                                                    />
                                                    <Button
                                                        icon="trash"
                                                        onClick={() => removeFAQ(index)}
                                                        size="small"
                                                        variant="secondary"
                                                        disabled={faqs.length <= 1}
                                                        label={__('Remove FAQ', 'puroclean')}
                                                        isDestructive
                                                    />
                                                </Flex>
                                            </FlexItem>
                                        </Flex>
                                    </CardHeader>
                                    
                                    {expandedFaq === index && (
                                        <CardBody>
                                            <TextControl
                                                label={__('Question', 'puroclean')}
                                                value={faq.question}
                                                onChange={(value) => updateFAQ(index, 'question', value)}
                                                placeholder={__('Enter your FAQ question...', 'puroclean')}
                                            />
                                            <Spacer />
                                            <TextareaControl
                                                label={__('Answer', 'puroclean')}
                                                value={faq.answer}
                                                onChange={(value) => updateFAQ(index, 'answer', value)}
                                                placeholder={__('Enter your FAQ answer...', 'puroclean')}
                                                rows={4}
                                            />
                                        </CardBody>
                                    )}
                                    
                                    {expandedFaq !== index && (
                                        <CardBody>
                                            <div className="text-sm">
                                                <strong>{faq.question || __('Untitled Question', 'puroclean')}</strong>
                                                <p className="text-gray-600 mt-1 line-clamp-2">
                                                    {faq.answer ? faq.answer.substring(0, 100) + (faq.answer.length > 100 ? '...' : '') : __('No answer provided', 'puroclean')}
                                                </p>
                                            </div>
                                        </CardBody>
                                    )}
                                </Card>
                            ))}
                        </div>

                        <div className="text-center mt-6">
                            <Button
                                variant="primary"
                                onClick={addFAQ}
                                icon="plus-alt2"
                            >
                                {__('Add New FAQ', 'puroclean')}
                            </Button>
                        </div>
                    </>
                ) : (
                    <div className="max-w-4xl mx-auto">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                            <h3 className="text-lg font-semibold text-blue-900 mb-2">
                                {__('FAQ Source: Custom Post Type', 'puroclean')}
                            </h3>
                            <div className="text-sm text-blue-800 space-y-1">
                                <p><strong>{__('Limit:', 'puroclean')}</strong> {faqLimit}</p>
                                <p><strong>{__('Category:', 'puroclean')}</strong> {faqCategory || __('All categories', 'puroclean')}</p>
                                <p><strong>{__('Order:', 'puroclean')}</strong> {__('Newest updated first', 'puroclean')}</p>
                            </div>
                        </div>
                        
                        <div className="text-center text-gray-600">
                            <p>{__('FAQs will be loaded from your FAQ posts based on the settings above.', 'puroclean')}</p>
                            <p className="text-sm mt-2">
                                {__('Switch to "Manual Entry" to edit individual FAQs directly.', 'puroclean')}
                            </p>
                        </div>
                        
                        <div className="text-center mt-4">
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                <p className="text-sm text-yellow-800 mb-2">
                                    {__('Need to add or edit FAQ posts?', 'puroclean')}
                                </p>
                                <a 
                                    href={window.siteGlobals?.adminUrl + 'edit.php?post_type=faq'}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-700 transition-colors"
                                >
                                    {__('Edit FAQ Entries', 'puroclean')}
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                    </svg>
                                </a>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
} 